/**
 * Template unique et standardisé pour la fiche de police.
 * Ce fichier est la seule source de vérité pour le rendu de la fiche de police.
 * Utilisé par: CheckInDialog, DocumentsSection, PrecheckinLink, GuestProfile
 */

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Génère le HTML de la fiche de police (template officiel validé)
 * @param {Object} data - Données de la fiche de police depuis l'API /api/reservations/:id/police-form
 * @returns {string} HTML complet de la fiche de police
 */
export const generatePoliceFormHTML = (data) => {
  const { establishment, reservation, main_guest, guests = [], generated_at, version } = data;
  
  return `
<!DOCTYPE html>
<html>
<head>
  <title>Fiche de Police / Police Form - ${main_guest?.first_name || ''} ${main_guest?.last_name || ''}</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 20px; max-width: 900px; margin: 0 auto; font-size: 11px; }
    h1 { text-align: center; margin-bottom: 5px; font-size: 20px; }
    h1 .subtitle { font-size: 14px; font-weight: normal; color: #666; }
    .header { border-bottom: 2px solid #333; padding-bottom: 10px; margin-bottom: 15px; }
    .establishment { text-align: center; margin-bottom: 10px; }
    .establishment-name { font-size: 16px; font-weight: bold; }
    .establishment-details { font-size: 11px; color: #444; }
    .section-title { font-size: 13px; font-weight: bold; border-bottom: 1px solid #333; padding-bottom: 3px; margin: 15px 0 10px 0; }
    .bilingual { color: #666; font-style: italic; }
    .reservation-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; background: #f8f8f8; padding: 10px; border-radius: 4px; margin-bottom: 10px; }
    .reservation-grid .item { }
    .reservation-grid .label { font-size: 9px; color: #666; display: block; }
    .reservation-grid .value { font-weight: bold; font-size: 11px; }
    .contact-box { background: #f0f7ff; padding: 10px; border-radius: 4px; border: 1px solid #cce0ff; margin-bottom: 15px; }
    .contact-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .contact-grid .label { font-size: 9px; color: #666; display: block; }
    .contact-grid .value { font-size: 11px; }
    .guest { border: 1px solid #ccc; padding: 10px; margin-bottom: 10px; border-radius: 4px; page-break-inside: avoid; }
    .guest-header { display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #eee; padding-bottom: 5px; margin-bottom: 8px; }
    .guest-name { font-weight: bold; font-size: 13px; }
    .guest-badges { display: flex; gap: 5px; }
    .guest-badge { background: #e8e8e8; padding: 2px 6px; border-radius: 3px; font-size: 9px; }
    .guest-badge.main { background: #d4edda; color: #155724; }
    .guest-badge.adult { background: #cce5ff; color: #004085; }
    .guest-badge.minor { background: #fff3cd; color: #856404; }
    .info-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-bottom: 10px; }
    .info-item .label { font-size: 9px; color: #666; display: block; }
    .info-item .value { font-size: 11px; }
    .photo-signature-row { display: flex; gap: 15px; border-top: 1px solid #eee; padding-top: 10px; }
    .photo-box { width: 120px; }
    .photo-box img { width: 100%; height: 80px; object-fit: cover; border: 1px solid #ddd; border-radius: 3px; }
    .photo-box .no-photo { width: 100%; height: 80px; border: 1px dashed #ccc; display: flex; align-items: center; justify-content: center; color: #999; font-size: 9px; text-align: center; }
    .signature-box { flex: 1; }
    .signature-area { border: 1px solid #999; height: 60px; margin-top: 3px; }
    .footer { margin-top: 20px; font-size: 10px; color: #666; text-align: center; border-top: 1px solid #ddd; padding-top: 10px; }
    .legal-notice { background: #fff8e6; border: 1px solid #ffe0a0; padding: 8px; border-radius: 4px; margin-top: 15px; font-size: 9px; }
    @media print { 
      body { padding: 10px; } 
      .no-print { display: none; }
      .guest { page-break-inside: avoid; }
    }
  </style>
</head>
<body>
  <div class="header">
    <h1>
      FICHE DE POLICE<br>
      <span class="subtitle">POLICE REGISTRATION FORM</span>
    </h1>
    <div class="establishment">
      <div class="establishment-name">${establishment?.name || 'Établissement'}</div>
      <div class="establishment-details">
        ${establishment?.address || ''}${establishment?.city ? `, ${establishment.city}` : ''}<br>
        ${establishment?.phone ? `Tél/Phone: ${establishment.phone}` : ''}
        ${establishment?.email ? ` | Email: ${establishment.email}` : ''}
      </div>
    </div>
  </div>

  <div class="section-title">
    Informations de séjour <span class="bilingual">/ Stay Information</span>
  </div>
  <div class="reservation-grid">
    <div class="item">
      <span class="label">N° Réservation / Booking Ref.</span>
      <span class="value">${reservation?.reservation_number || reservation?.id || '-'}</span>
    </div>
    <div class="item">
      <span class="label">Date d'arrivée / Check-in</span>
      <span class="value">${reservation?.check_in || '-'}</span>
    </div>
    <div class="item">
      <span class="label">Date de départ / Check-out</span>
      <span class="value">${reservation?.check_out || '-'}</span>
    </div>
    <div class="item">
      <span class="label">Durée / Duration</span>
      <span class="value">${reservation?.nights || 0} nuit(s) / night(s)</span>
    </div>
    <div class="item">
      <span class="label">Adultes / Adults</span>
      <span class="value">${reservation?.adults || 0}</span>
    </div>
    <div class="item">
      <span class="label">Enfants / Children</span>
      <span class="value">${reservation?.children || 0}</span>
    </div>
    <div class="item">
      <span class="label">Total voyageurs / Total guests</span>
      <span class="value">${reservation?.total_pax || 0}</span>
    </div>
    <div class="item">
      <span class="label">Réf. plateforme / Platform Ref.</span>
      <span class="value">${reservation?.platform_ref || '-'}</span>
    </div>
  </div>

  ${main_guest ? `
  <div class="section-title">
    Contact responsable <span class="bilingual">/ Main Contact</span>
  </div>
  <div class="contact-box">
    <div class="contact-grid">
      <div>
        <span class="label">Nom complet / Full Name</span>
        <span class="value"><strong>${main_guest.first_name || ''} ${main_guest.last_name || ''}</strong></span>
      </div>
      <div>
        <span class="label">Téléphone / Phone</span>
        <span class="value">${main_guest.phone || '-'}</span>
      </div>
      <div>
        <span class="label">Email</span>
        <span class="value">${main_guest.email || '-'}</span>
      </div>
      <div>
        <span class="label">Pays / Country</span>
        <span class="value">${main_guest.country || '-'}</span>
      </div>
    </div>
  </div>
  ` : ''}

  <div class="section-title">
    Voyageurs enregistrés (${guests.length}) <span class="bilingual">/ Registered Guests</span>
  </div>
  ${guests.length > 0 ? guests.map((g, i) => {
    const photoUrl = g.passport_photo 
      ? (g.passport_photo.startsWith('http') ? g.passport_photo : `${API_URL}${g.passport_photo}`)
      : null;
    return `
    <div class="guest">
      <div class="guest-header">
        <div class="guest-name">${i + 1}. ${(g.last_name || '').toUpperCase()} ${g.first_name || ''}</div>
        <div class="guest-badges">
          ${g.is_main ? '<span class="guest-badge main">Principal / Main</span>' : ''}
          <span class="guest-badge ${g.is_adult !== false ? 'adult' : 'minor'}">${g.is_adult !== false ? `Adulte / Adult${g.age ? ` (${g.age} ans/yrs)` : ''}` : `Mineur / Minor${g.age ? ` (${g.age} ans/yrs)` : ''}`}</span>
        </div>
      </div>
      <div class="info-grid">
        <div class="info-item">
          <span class="label">Date de naissance / Birth Date</span>
          <span class="value">${g.birth_date || '-'}</span>
        </div>
        <div class="info-item">
          <span class="label">Nationalité / Nationality</span>
          <span class="value">${g.nationality || '-'}</span>
        </div>
        <div class="info-item">
          <span class="label">Type document / Doc. Type</span>
          <span class="value">${g.document_type === 'PASSPORT' ? 'Passeport' : (g.document_type || 'Passeport')}</span>
        </div>
        <div class="info-item">
          <span class="label">N° Document / Doc. Number</span>
          <span class="value"><strong>${g.document_number || '-'}</strong></span>
        </div>
        <div class="info-item">
          <span class="label">Expiration / Expiry</span>
          <span class="value">${g.document_expiry || '-'}</span>
        </div>
      </div>
      <div class="photo-signature-row">
        <div class="photo-box">
          <span class="label">Photo passeport / Passport Photo</span>
          ${photoUrl ? `<img src="${photoUrl}" alt="Passeport" onerror="this.style.display='none'">` : '<div class="no-photo">Pas de photo<br>No photo</div>'}
        </div>
        <div class="signature-box">
          <span class="label">Signature du voyageur / Guest Signature</span>
          <div class="signature-area"></div>
        </div>
      </div>
    </div>
  `}).join('') : '<p style="text-align: center; color: #666; padding: 20px;">Aucun voyageur enregistré / No registered guests</p>'}

  <div class="legal-notice">
    <strong>Mentions légales / Legal Notice:</strong><br>
    Conformément à la législation marocaine, tout établissement d'hébergement est tenu de tenir un registre des voyageurs et de le présenter aux autorités sur demande. Les informations collectées sont utilisées exclusivement à des fins de sécurité publique.<br>
    <span class="bilingual">In accordance with Moroccan law, all accommodation establishments are required to maintain a guest register and present it to authorities upon request. The information collected is used exclusively for public safety purposes.</span>
  </div>

  <div class="footer">
    Document généré le / Generated on: ${generated_at ? new Date(generated_at).toLocaleString('fr-FR') : new Date().toLocaleString('fr-FR')} | Version ${version || '1.0'}
  </div>
  <div class="no-print" style="text-align: center; margin-top: 20px;">
    <button onclick="window.print()" style="padding: 12px 24px; font-size: 16px; cursor: pointer; background: #2563eb; color: white; border: none; border-radius: 8px;">
      Imprimer / Print
    </button>
  </div>
</body>
</html>
  `;
};

/**
 * Ouvre une fenêtre d'impression avec la fiche de police
 * @param {Object} data - Données de la fiche de police depuis l'API
 */
export const printPoliceForm = (data) => {
  const html = generatePoliceFormHTML(data);
  const printWindow = window.open('', '_blank');
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
  }
};

export default generatePoliceFormHTML;
